import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import TextField, { TextFieldProps } from '@mui/material/TextField';

// ----------------------------------------------------------------------

type Props = Omit<TextFieldProps, 'type'> & {
  name: string;
  hideHelperText?: boolean;
  /** Use 'numeric' for mobile-friendly text input with numeric keyboard (stores as string/null) */
  type?: TextFieldProps['type'] | 'numeric';
};
export function RHFTextField({
  name,
  hideHelperText,
  helperText,
  type,
  inputProps,
  multiline,
  ...other
}: Props) {
  const { control } = useFormContext();
  const labelId = useId();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          multiline={multiline}
          type={type === 'numeric' ? 'text' : type}
          value={type === 'number' && field.value === 0 ? '' : (field.value ?? '')}
          onChange={(event) => {
            const { value } = event.target;
            if (type === 'number') {
              field.onChange(value === '' ? 0 : Number(value));
            } else if (type === 'numeric') {
              field.onChange(value === '' ? null : value);
            } else {
              field.onChange(value);
            }
          }}
          error={!!error}
          helperText={!hideHelperText && error ? error?.message : helperText}
          // Accessibility id breaks multiline (inserts duplicate textarea)
          inputProps={
            multiline
              ? type === 'numeric'
                ? { inputMode: 'numeric', ...inputProps }
                : inputProps
              : {
                  id: labelId,
                  ...(type === 'numeric' ? { inputMode: 'numeric' } : {}),
                  ...inputProps,
                }
          }
          InputLabelProps={multiline ? undefined : { htmlFor: labelId }}
          {...other}
        />
      )}
    />
  );
}
